import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Card, CardContent, CardMedia,
  Grid, Chip, CircularProgress, Alert, Stepper, Step, StepLabel,
  InputAdornment, Divider
} from '@mui/material';
import {
  DirectionsCar, CalendarMonth, Person, Email, Phone,
  CheckCircle, Search, ArrowBack, ArrowForward, LocationOn
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const steps = ['Buscar Veículos', 'Escolher Veículo', 'Seus Dados', 'Confirmação'];

export default function ReservaPublica() {
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState(null);

  // Step 0 — busca
  const [busca, setBusca] = useState({
    local: searchParams.get('local') || '',
    retirada: searchParams.get('retirada') || '',
    devolucao: searchParams.get('devolucao') || ''
  });

  // Step 1 — veículos
  const [veiculos, setVeiculos] = useState([]);
  const [veiculoSelecionado, setVeiculoSelecionado] = useState(null);

  // Step 2 — dados do cliente
  const [cliente, setCliente] = useState({ nome: '', email: '', telefone: '', observacoes: '' });
  const [errorsCliente, setErrorsCliente] = useState({});

  // Se veio com datas preenchidas, buscar automaticamente
  useEffect(() => {
    if (busca.retirada && busca.devolucao) {
      buscarVeiculos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function buscarVeiculos() {
    if (!busca.retirada || !busca.devolucao) {
      setErro('Informe as datas de retirada e devolução');
      return;
    }
    if (busca.retirada >= busca.devolucao) {
      setErro('A data de devolução deve ser após a data de retirada');
      return;
    }
    setErro('');
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/pre-reservas/veiculos-disponiveis`, {
        params: { retirada: busca.retirada, devolucao: busca.devolucao }
      });
      setVeiculos(res.data.veiculos || []);
      setActiveStep(1);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao buscar veículos disponíveis');
    } finally {
      setLoading(false);
    }
  }

  function selecionarVeiculo(v) {
    setVeiculoSelecionado(v);
    setActiveStep(2);
  }

  function validarCliente() {
    const errs = {};
    if (!cliente.nome.trim()) errs.nome = 'Nome obrigatório';
    if (!cliente.email.trim()) errs.email = 'E-mail obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cliente.email)) errs.email = 'E-mail inválido';
    if (!cliente.telefone.trim()) errs.telefone = 'Telefone obrigatório';
    setErrorsCliente(errs);
    return Object.keys(errs).length === 0;
  }

  async function confirmarReserva() {
    if (!validarCliente()) return;
    setEnviando(true);
    setErro('');
    try {
      const res = await axios.post(`${API_URL}/pre-reservas`, {
        nome: cliente.nome,
        email: cliente.email,
        telefone: cliente.telefone,
        observacoes: cliente.observacoes,
        id_veiculo: veiculoSelecionado.id,
        data_retirada: busca.retirada,
        data_devolucao: busca.devolucao,
        local_retirada: busca.local
      });
      setSucesso(res.data);
      setActiveStep(3);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao enviar pré-reserva');
    } finally {
      setEnviando(false);
    }
  }

  const dias = busca.retirada && busca.devolucao
    ? Math.max(0, Math.ceil((new Date(busca.devolucao) - new Date(busca.retirada)) / (1000 * 60 * 60 * 24)))
    : 0;

  const formatarData = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#FF8C00', py: 3, px: 3, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
          🚗 Reserva de Veículo
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
          Escolha seu veículo, preencha seus dados e aguarde a confirmação
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4, pt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {erro && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErro('')}>{erro}</Alert>}

        {/* STEP 0: BUSCA */}
        {activeStep === 0 && (
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Quando você precisa do veículo?
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Local de Retirada"
                    placeholder="Cidade, aeroporto ou endereço"
                    value={busca.local}
                    onChange={(e) => setBusca({ ...busca, local: e.target.value })}
                    InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn sx={{ color: '#FF8C00' }} /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data de Retirada *"
                    type="date"
                    value={busca.retirada}
                    onChange={(e) => setBusca({ ...busca, retirada: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonth sx={{ color: '#FF8C00' }} /></InputAdornment> }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data de Devolução *"
                    type="date"
                    value={busca.devolucao}
                    onChange={(e) => setBusca({ ...busca, devolucao: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: busca.retirada || new Date().toISOString().split('T')[0] }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><CalendarMonth sx={{ color: '#FF8C00' }} /></InputAdornment> }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Search />}
                  onClick={buscarVeiculos}
                  disabled={loading}
                  sx={{ bgcolor: '#FF8C00', '&:hover': { bgcolor: '#E67E00' }, px: 4 }}
                >
                  {loading ? 'Buscando...' : 'Buscar Veículos'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* STEP 1: LISTA DE VEÍCULOS */}
        {activeStep === 1 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {veiculos.length === 0
                  ? 'Nenhum veículo disponível para esse período'
                  : `${veiculos.length} veículo${veiculos.length !== 1 ? 's' : ''} disponíve${veiculos.length !== 1 ? 'is' : 'l'}`}
              </Typography>
              <Button startIcon={<ArrowBack />} onClick={() => setActiveStep(0)} size="small">
                Alterar Datas
              </Button>
            </Box>

            {/* Resumo da busca */}
            <Card sx={{ mb: 3, bgcolor: '#fff8e1', border: '1px solid #FFD54F' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {busca.local && <Typography variant="body2"><strong>📍</strong> {busca.local}</Typography>}
                  <Typography variant="body2"><strong>📅 Retirada:</strong> {formatarData(busca.retirada)}</Typography>
                  <Typography variant="body2"><strong>📅 Devolução:</strong> {formatarData(busca.devolucao)}</Typography>
                  <Typography variant="body2"><strong>🕐</strong> {dias} dia{dias !== 1 ? 's' : ''}</Typography>
                </Box>
              </CardContent>
            </Card>

            {veiculos.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <DirectionsCar sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">Nenhum veículo disponível</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Tente alterar as datas de retirada e devolução
                  </Typography>
                  <Button variant="outlined" onClick={() => setActiveStep(0)} sx={{ mt: 3, borderColor: '#FF8C00', color: '#FF8C00' }}>
                    Alterar Datas
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Grid container spacing={2}>
                {veiculos.map((v) => (
                  <Grid item xs={12} md={6} key={v.id}>
                    <Card
                      sx={{
                        cursor: 'pointer', transition: 'all 0.2s',
                        '&:hover': { boxShadow: '0 4px 20px rgba(255,140,0,0.25)', transform: 'translateY(-2px)' }
                      }}
                      onClick={() => selecionarVeiculo(v)}
                    >
                      {v.foto_url && (
                        <CardMedia
                          component="img"
                          height={160}
                          image={v.foto_url}
                          alt={v.modelo}
                          sx={{ objectFit: 'cover' }}
                        />
                      )}
                      {!v.foto_url && (
                        <Box sx={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5' }}>
                          <DirectionsCar sx={{ fontSize: 64, color: '#ccc' }} />
                        </Box>
                      )}
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{v.modelo}</Typography>
                        <Typography variant="body2" color="text.secondary">{v.marca} · {v.ano} · {v.cor}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
                          {v.cambio && <Chip label={v.cambio} size="small" />}
                          {v.ar_condicionado ? <Chip label="Ar cond." size="small" color="info" /> : null}
                          {v.capacidade_passageiros && <Chip label={`${v.capacidade_passageiros} lugares`} size="small" />}
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="h6" sx={{ color: '#FF8C00', fontWeight: 700 }}>
                              R$ {Number(v.preco_dia || 0).toFixed(2)}
                              <Typography component="span" variant="caption" color="text.secondary"> /dia</Typography>
                            </Typography>
                            {dias > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                Total estimado: R$ {(Number(v.preco_dia || 0) * dias).toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            endIcon={<ArrowForward />}
                            sx={{ bgcolor: '#FF8C00', '&:hover': { bgcolor: '#E67E00' } }}
                          >
                            Selecionar
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* STEP 2: DADOS DO CLIENTE */}
        {activeStep === 2 && veiculoSelecionado && (
          <Box>
            <Button startIcon={<ArrowBack />} onClick={() => setActiveStep(1)} size="small" sx={{ mb: 2 }}>
              Voltar
            </Button>

            {/* Resumo do veículo */}
            <Card sx={{ mb: 3, border: '2px solid #FF8C00' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Veículo selecionado</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{veiculoSelecionado.modelo}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatarData(busca.retirada)} → {formatarData(busca.devolucao)} ({dias} dia{dias !== 1 ? 's' : ''})
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ color: '#FF8C00', fontWeight: 700 }}>
                    R$ {(Number(veiculoSelecionado.preco_dia || 0) * dias).toFixed(2)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Seus dados</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nome completo *"
                      value={cliente.nome}
                      onChange={(e) => setCliente({ ...cliente, nome: e.target.value })}
                      error={!!errorsCliente.nome}
                      helperText={errorsCliente.nome}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: '#FF8C00' }} /></InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="E-mail *"
                      type="email"
                      value={cliente.email}
                      onChange={(e) => setCliente({ ...cliente, email: e.target.value })}
                      error={!!errorsCliente.email}
                      helperText={errorsCliente.email || 'Você receberá a confirmação neste e-mail'}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: '#FF8C00' }} /></InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Telefone / WhatsApp *"
                      value={cliente.telefone}
                      onChange={(e) => setCliente({ ...cliente, telefone: e.target.value })}
                      error={!!errorsCliente.telefone}
                      helperText={errorsCliente.telefone}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ color: '#FF8C00' }} /></InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Observações (opcional)"
                      multiline
                      rows={2}
                      value={cliente.observacoes}
                      onChange={(e) => setCliente({ ...cliente, observacoes: e.target.value })}
                      placeholder="Alguma informação adicional?"
                    />
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 3 }}>
                  💰 <strong>Pagamento presencial:</strong> Não é necessário pagar nada agora. O pagamento é feito na retirada do veículo. Após enviar, nossa equipe confirmará a disponibilidade e você receberá um e-mail.
                </Alert>

                <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={() => setActiveStep(1)}>Voltar</Button>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                    onClick={confirmarReserva}
                    disabled={enviando}
                    sx={{ bgcolor: '#FF8C00', '&:hover': { bgcolor: '#E67E00' }, px: 4 }}
                  >
                    {enviando ? 'Enviando...' : 'Solicitar Pré-Reserva'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* STEP 3: SUCESSO */}
        {activeStep === 3 && sucesso && (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <CheckCircle sx={{ fontSize: 80, color: '#2e7d32', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32', mb: 1 }}>
                Pré-Reserva Enviada!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {sucesso.mensagem}
              </Typography>
              <Card sx={{ bgcolor: '#f5f5f5', maxWidth: 400, mx: 'auto', mb: 3 }}>
                <CardContent>
                  <Typography variant="body2"><strong>📋 Número:</strong> #{sucesso.id}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}><strong>🚗 Veículo:</strong> {veiculoSelecionado?.modelo}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}><strong>📅</strong> {formatarData(busca.retirada)} → {formatarData(busca.devolucao)}</Typography>
                </CardContent>
              </Card>
              <Alert severity="success" sx={{ maxWidth: 500, mx: 'auto', textAlign: 'left' }}>
                📧 Um e-mail foi enviado para <strong>{cliente.email}</strong>. Nossa equipe irá analisar e confirmar em breve.
              </Alert>
              <Button
                variant="outlined"
                sx={{ mt: 4, borderColor: '#FF8C00', color: '#FF8C00' }}
                onClick={() => {
                  setActiveStep(0);
                  setVeiculoSelecionado(null);
                  setCliente({ nome: '', email: '', telefone: '', observacoes: '' });
                  setSucesso(null);
                }}
              >
                Fazer outra reserva
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', py: 3, mt: 4, borderTop: '1px solid #e0e0e0' }}>
        <Typography variant="caption" color="text.disabled">
          Powered by Easy Locadora
        </Typography>
      </Box>
    </Box>
  );
}
